// ============================================================
// EnvironmentBanner.tsx
// Barra de ambiente para DEV e UAT
// Coloque em: FillSign.App/src/components/EnvironmentBanner.tsx
// ============================================================

import React, { useState, useEffect } from 'react';

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';
const HIDE_DURATION = 60 * 60 * 1000; // 1 hora em milissegundos
const STORAGE_KEY = 'env-banner-hidden-until';

interface EnvironmentConfig {
  show: boolean;
  label: string;
  bgColor: string;
  textColor: string;
  icon: string;
}

const getEnvironmentConfig = (): EnvironmentConfig => {
  switch (ENVIRONMENT.toLowerCase()) {
    case 'development':
    case 'dev':
      return {
        show: true,
        label: '🚧 AMBIENTE DE DESENVOLVIMENTO (SANDBOX)',
        bgColor: 'bg-green-600',
        textColor: 'text-white',
        icon: '🔧',
      };
    case 'staging':
    case 'uat':
      return {
        show: true,
        label: '🧪 AMBIENTE DE HOMOLOGAÇÃO (UAT)',
        bgColor: 'bg-blue-600',
        textColor: 'text-white',
        icon: '🧪',
      };
    case 'production':
    case 'prod':
    default:
      return {
        show: false,
        label: '',
        bgColor: '',
        textColor: '',
        icon: '',
      };
  }
};

export const EnvironmentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const config = getEnvironmentConfig();

  useEffect(() => {
    if (!config.show) return;

    const checkVisibility = () => {
      const hiddenUntil = localStorage.getItem(STORAGE_KEY);
      if (hiddenUntil) {
        const hiddenUntilTime = parseInt(hiddenUntil, 10);
        if (Date.now() < hiddenUntilTime) {
          setIsVisible(false);
          const remaining = hiddenUntilTime - Date.now();
          const minutes = Math.ceil(remaining / 60000);
          setTimeLeft(`${minutes}min`);
          return;
        }
      }
      setIsVisible(true);
      setTimeLeft('');
    };

    checkVisibility();
    const interval = setInterval(checkVisibility, 30000); // Verifica a cada 30s

    return () => clearInterval(interval);
  }, [config.show]);

  const handleHide = () => {
    const hideUntil = Date.now() + HIDE_DURATION;
    localStorage.setItem(STORAGE_KEY, hideUntil.toString());
    setIsVisible(false);
    setTimeLeft('60min');
  };

  const handleShow = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsVisible(true);
    setTimeLeft('');
  };

  if (!config.show) return null;

  const short = ENVIRONMENT.toLowerCase() === "dev" || ENVIRONMENT.toLowerCase() === "development" ? "Sandbox" : "Homologação";
  const envTag = ENVIRONMENT.toLowerCase() === "development" ? "dev" : ENVIRONMENT.toLowerCase() === "staging" ? "uat" : ENVIRONMENT.toLowerCase();

  // Hidden for 1h: a dot in the corner brings it back.
  if (!isVisible) {
    return (
      <button
        onClick={handleShow}
        className={`fixed bottom-3 left-3 z-40 h-3 w-3 rounded-full ${config.bgColor} shadow ring-2 ring-white/70`}
        title={`${short} · ${envTag} (${timeLeft})`}
        aria-label={`Mostrar indicador de ambiente (${short})`}
      />
    );
  }

  // Compact pill: keeps the environment colour, stays out of the way of the header.
  return (
    <div
      className={`fixed bottom-3 left-3 z-40 flex items-center gap-2 rounded-full ${config.bgColor} ${config.textColor} pl-3 pr-1.5 py-1 text-xs font-medium shadow-lg`}
      role="status"
      aria-live="polite"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
      <span>{short}</span>
      <span className="opacity-70">· {envTag}</span>
      <button
        onClick={handleHide}
        className="ml-1 flex h-5 w-5 items-center justify-center rounded-full hover:bg-white/25 transition-colors"
        title="Ocultar por 1 hora"
        aria-label="Ocultar por 1 hora"
      >
        ✕
      </button>
    </div>
  );
};

// ============================================================
// Hook para verificar se é ambiente de teste
// ============================================================
export const useIsTestEnvironment = (): boolean => {
  const env = ENVIRONMENT.toLowerCase();
  return env === 'development' || env === 'dev' || env === 'staging' || env === 'uat';
};

export const getEnvironmentName = (): string => {
  const env = ENVIRONMENT.toLowerCase();
  switch (env) {
    case 'development':
    case 'dev':
      return 'DEV';
    case 'staging':
    case 'uat':
      return 'UAT';
    default:
      return 'PROD';
  }
};

export default EnvironmentBanner;
